import { useContext, useState, useEffect, useCallback } from 'react';
import { JSONSchema7 } from 'json-schema';
import { useDrop } from 'react-dnd';
import { DragTypes } from '../DragAndDrop';
import { useSnackbar } from 'notistack';
import update from 'immutability-helper';
import JSONSchema7Properties from '../Shared/jSONSchema7Properties';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import { InputButton } from '@abbott-components/core';
import {
  WorkflowEngineContext,
  WorkflowEngineActionKind,
  UpdatePages
} from '../WorkflowEngine/workflowEngineReducer';
import { DraggablePageListItem } from '../DragAndDrop';
import PageState from '../Shared/pageState';
import saveRetrievedPageUpgrades from '../WorkflowEngine/SchemaUpgrade/saveRetrievedPageUpgrades';

interface PageProps {
  id: string;
  name: string;
}

export default ({
  openDialog,
  closeDialog
}: {
  openDialog: boolean;
  closeDialog: () => void;
}) => {
  const {
    state: { pages },
    dispatch
  } = useContext(WorkflowEngineContext);

  const [pageList, setPageList] = useState<PageProps[]>([]);

  const { enqueueSnackbar } = useSnackbar();

  const getPageName = (pageSchema: JSONSchema7): string => {
    const controlProperties = pageSchema.properties?.page as JSONSchema7;
    const pageProps = controlProperties.properties as JSONSchema7Properties;
    if (pageProps && pageProps.label) {
      return pageProps.label.default as string;
    }
    return '';
  };

  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setPageList((prevCards: PageProps[]) =>
      update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex] as PageProps]
        ]
      })
    );
  }, []);

  const renderPageListItem = useCallback((page: PageProps, index: number) => {
    return (
      <DraggablePageListItem
        key={page.id}
        pageProps={page}
        index={index}
        moveCard={moveCard}
      />
    );
  }, []);

  const handleSavePages = async () => {
    const newOrderedPages = pageList.map((order) => {
      return pages.find((page) => page.id === order.id);
    }) as PageState[];

    const putResponse = await saveRetrievedPageUpgrades(newOrderedPages);
    if (putResponse) {
      enqueueSnackbar('Reorder successfully saved!', {
        variant: 'success'
      });
    } else {
      enqueueSnackbar('Reorder failed, please try again later.', {
        variant: 'error'
      });
    }

    const payload = {
      newPages: putResponse ? newOrderedPages : pages,
      lastModifyDate: putResponse?.lastModifyDate,
      lastModifyBy: putResponse?.lastModifyBy
    };

    dispatch({
      type: WorkflowEngineActionKind.REORDER_PAGES,
      payload: payload as UpdatePages
    });

    closeDialog();
  };

  const [, drop] = useDrop(() => ({
    accept: DragTypes.PAGE_LIST_ITEM
  }));

  useEffect(() => {
    const initialList: PageProps[] = pages.map((page) => {
      return {
        id: page.id,
        name: getPageName(page.schema)
      };
    });

    setPageList(initialList);
  }, []);

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={closeDialog}
        fullWidth={true}
        maxWidth={'md'}
        scroll="paper"
      >
        <DialogTitle
          sx={{ textAlign: 'center', fontSize: '20px', fontWeight: 'bolder' }}
        >
          Reorder Pages
        </DialogTitle>

        <DialogContent ref={drop}>
          <Stack spacing={3}>
            {pageList.map((page, index) => renderPageListItem(page, index))}
          </Stack>
        </DialogContent>

        <DialogActions>
          <InputButton
            name="cancelReorder"
            type="button"
            variant="text"
            onClick={closeDialog}
          >
            Cancel
          </InputButton>
          <InputButton
            name="saveReorder"
            type="button"
            variant="contained"
            onClick={handleSavePages}
          >
            Save
          </InputButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
